import { liveReload } from '@shared/services/realtime.service';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { ListView } from '@app/shared/utils/list-view';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { ModulesService } from '@shared/services/modules.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { GaPdfService } from '@app/shared/pdf/ga-pdf.service';

import { TransferRequestsService } from '../logistics.service';
import { LogisticsCatalogs, MaterialRequestsService, TransfersService, fmtDate, statusChip, errMsg } from '../logistics.service';
import { TransferFormDialogComponent } from './transfer-form-dialog.component';
import { TransferReceiveDialogComponent } from './transfer-receive-dialog.component';
import { DhlMailDialogComponent } from './dhl-mail-dialog.component';
import { PickDialogComponent } from '../pick-dialog.component';
import { buildTransferDoc, transferPdfData } from './transfer-pdf';

export const TRANSFER_STATUSES = ['Borrador', 'Por recolectar', 'En tránsito', 'Recibido', 'Cancelado'];

/**
 * Traspaso de material entre cuentas (TRA-n): locker ↔ in-house del mismo
 * hospital (inmediato) o entre hospitales (DHL: recolección → tránsito →
 * recepción). Mesa de control recibe aviso de cada paso.
 */
@Component({
  selector: 'app-transfers',
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, MatTableModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule, MatButtonToggleModule, MatDatepickerModule, MatDialogModule,
    KpiStripComponent, PageLoaderComponent, PageTitleComponent, SearchPillComponent, FilterChipsComponent],
  templateUrl: './transfers.component.html',
  styleUrl: '../logistics.scss',
})
export class TransfersComponent implements OnInit {
  private readonly modules = inject(ModulesService);
  readonly title = this.modules.moduleTitle('/transfers', 'Traspaso de material');

  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransfersService);
  private readonly requests = inject(MaterialRequestsService);
  private readonly dialog = inject(MatDialog);
  private readonly msg = inject(CustomMessageService);
  private readonly excel = inject(SimpleExcelService);
  private readonly pdf = inject(GaPdfService);
  private readonly scopeService = inject(UserScopeService);
  private scope: UserScope = { places: null, specialties: null };
  private requestFolios: Record<string, string> = {};
  private readonly trService = inject(TransferRequestsService);

  readonly statuses = TRANSFER_STATUSES;
  readonly fmtDate = fmtDate;
  readonly statusChip = statusChip;
  readonly listView = new ListView('transfers', 'table');
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['transfers', 'transferRequests'], () => this.pageLoad.ready && this.pageLoad.refresh(this.load()));

  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  statusFilter = '';
  placeFilter = '';
  searchText = '';

  columns = ['folio', 'createdOn', 'origin', 'destination', 'pieces', 'dhl', 'status', 'actions'];
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI: Record<string, (r: any) => boolean> = {
    draft: r => r.status === 'Borrador',
    pickup: r => r.status === 'Por recolectar',
    transit: r => r.status === 'En tránsito',
    done: r => r.status === 'Recibido',
    missing: r => r._missing > 0,
  };

  get placeOptions(): any[] { return this.cat.places.filter(p => inScope(this.scope.places, p.id, false)); }

  ngOnInit(): void { void this.init(); }
  init(): Promise<boolean> { return this.pageLoad.run(() => this.initialLoad()); }

  private async initialLoad(): Promise<void> {
    try { this.scope = await this.scopeService.load(); } catch { /* sin alcance */ }
    await this.cat.load();
    await this.load();
  }

  async load(): Promise<void> {
    try {
      const [r, q, sot]: any[] = await Promise.all([
        this.service.search({ searchType: 'default', condition: { deleted: false } }),
        this.requests.search({ searchType: 'default', condition: { deleted: false } }).catch(() => null),
        this.trService.search({ searchType: 'default', condition: { deleted: false } }).catch(() => null),
      ]);
      this.requestFolios = Object.fromEntries([...((q?.data ?? []) as any[]), ...((sot?.data ?? []) as any[])].map(x => [x.id, x.folio]));
      this.allRows = ((r?.data ?? []) as any[])
        // Alcance: se ve si el origen o el destino es de sus hospitales.
        .filter(x => !this.scope.places || inScope(this.scope.places, x.originPlace, false) || inScope(this.scope.places, x.destinationPlace, false))
        .filter(x => !this.placeFilter || x.originPlace === this.placeFilter || x.destinationPlace === this.placeFilter)
        .filter(x => !this.statusFilter || x.status === this.statusFilter)
        .filter(x => inDateRange(x.createdOn, this.periodStart, this.periodEnd))
        .map(x => ({
          ...x,
          _origin: this.cat.warehouseLabelById(x.originWarehouse), _destination: this.cat.warehouseLabelById(x.destinationWarehouse),
          _pieces: (x.items ?? []).length, _missing: (x.items ?? []).filter((i: any) => i.missing).length,
          _request: (x.request ? this.requestFolios[x.request] : x.transferRequest ? this.requestFolios[x.transferRequest] : '') ?? '',
          _codes: (x.items ?? []).map((i: any) => `${i.code} ${i.tagRfid ?? ''}`).join(' '),
        }))
        .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.dataSource.filterPredicate = (row, f) =>
        [row.folio, row._origin, row._destination, row.dhlGuide, row._request, row._codes, row.status].join(' ').toLowerCase().includes(f);
      this.refreshKpis();
      this.dataSource.paginator = this.paginator;
      this.dataSource.filter = this.searchText.trim().toLowerCase();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar los traspasos.'), dismissBtn: true });
    }
  }

  private refreshKpis(): void {
    const c = (k: string) => this.allRows.filter(TransfersComponent.KPI[k]).length;
    this.kpis = [
      { key: 'total', label: 'Traspasos', icon: 'swap_horiz', value: this.allRows.length, isTotal: true },
      { key: 'draft', label: 'Borradores', icon: 'edit_note', value: c('draft') },
      { key: 'pickup', label: 'Por recolectar', icon: 'inventory', value: c('pickup'), tone: 'warn' },
      { key: 'transit', label: 'En tránsito', icon: 'local_shipping', value: c('transit') },
      { key: 'done', label: 'Recibidos', icon: 'task_alt', value: c('done'), tone: 'ok' },
      { key: 'missing', label: 'Con faltantes', icon: 'report', value: c('missing'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(k: string | null): void { this.activeKpi = k; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? TransfersComponent.KPI[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  applySearch(v: string): void { this.searchText = v ?? ''; this.dataSource.filter = this.searchText.trim().toLowerCase(); }

  get filterChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.placeFilter) c.push({ key: 'place', label: 'Hospital', value: this.cat.placeName(this.placeFilter) });
    if (this.statusFilter) c.push({ key: 'status', label: 'Estado', value: this.statusFilter });
    if (this.periodStart || this.periodEnd) c.push({ key: 'period', label: 'Periodo', value: `${fmtDate(this.periodStart, false) || '…'} – ${fmtDate(this.periodEnd, false) || '…'}`, removable: false });
    return c;
  }
  async removeChip(k: string): Promise<void> {
    if (k === 'place') this.placeFilter = '';
    if (k === 'status') this.statusFilter = '';
    await this.load();
  }
  async clearFilters(): Promise<void> {
    this.placeFilter = ''; this.statusFilter = '';
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    this.applySearch('');
    await this.load();
  }

  private clean(r: any): any {
    const out: any = {};
    for (const [k, v] of Object.entries(r)) if (!k.startsWith('_')) out[k] = v;
    return out;
  }

  private afterDialog(ok: any): void { if (ok) void this.load(); }

  openForm(t?: any): void {
    this.dialog.open(TransferFormDialogComponent, {
      data: { transfer: t ? this.clean(t) : undefined }, width: '880px', maxWidth: '96vw', maxHeight: '92vh',
    }).afterClosed().subscribe(ok => this.afterDialog(ok));
  }

  async ship(t: any): Promise<void> {
    const same = t.originPlace === t.destinationPlace;
    const yes = await this.msg.open({ title: 'Enviar traspaso', type: 'warning', acceptBtn: true, dismissBtn: true,
      acceptBtnText: 'Enviar', dismissBtnText: 'No',
      message: same ? `Las ${t._pieces} pieza(s) de <b>${t.folio}</b> pasarán de inmediato al destino.`
                    : `Las ${t._pieces} pieza(s) de <b>${t.folio}</b> quedarán en tránsito y se avisará a mesa de control para la recolección con DHL.` });
    if (!yes) return;
    try {
      const r = await this.service.ship(t.id);
      await this.msg.open({ title: '¡Listo!', type: 'success', duration: 2600,
        message: r?.status === 'Recibido' ? `Traspaso <b>${t.folio}</b> hecho: inventario actualizado.` : `Traspaso <b>${t.folio}</b> listo para recolección.` });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'No se envió', type: 'error', message: errMsg(err, 'No se pudo enviar el traspaso.'), dismissBtn: true });
    }
  }

  dhlMail(t: any): void {
    this.dialog.open(DhlMailDialogComponent, { data: { transfer: this.clean(t), requestFolio: t._request }, maxWidth: '95vw', maxHeight: '92vh' })
      .afterClosed().subscribe(ok => this.afterDialog(ok));
  }

  guide(t: any): void {
    this.dialog.open(PickDialogComponent, { data: {
      title: `Guía DHL · ${t.folio}`, mode: 'text', label: 'Número de guía', value: t.dhlGuide ?? '', required: true,
      message: 'Al capturar la guía, el traspaso pasa a <b>En tránsito</b>.', acceptLabel: 'Guardar guía',
    } }).afterClosed().subscribe(async (guide: string | undefined) => {
      if (!guide) return;
      try {
        await this.service.update({ ...this.clean(t), dhlGuide: guide, operationType: 'single' });
        await this.load();
      } catch (err) {
        await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo guardar la guía.'), dismissBtn: true });
      }
    });
  }

  receive(t: any): void {
    this.dialog.open(TransferReceiveDialogComponent, { data: { transfer: this.clean(t) }, maxWidth: '95vw', maxHeight: '92vh' })
      .afterClosed().subscribe(ok => this.afterDialog(ok));
  }

  async cancel(t: any): Promise<void> {
    const back = t.status === 'Por recolectar' ? ' Las piezas regresan a disponibles en el origen.' : '';
    const yes = await this.msg.open({ title: 'Cancelar traspaso', type: 'warning', acceptBtn: true, dismissBtn: true,
      acceptBtnText: 'Sí, cancelar', dismissBtnText: 'No', message: `¿Cancelar el traspaso <b>${t.folio}</b>?${back}` });
    if (!yes) return;
    try {
      await this.service.update({ ...this.clean(t), status: 'Cancelado', operationType: 'single' });
      await this.load();
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo cancelar.'), dismissBtn: true });
    }
  }

  downloadPdf(t: any): void {
    this.pdf.download(buildTransferDoc(transferPdfData(t, this.cat, t._request)), `Traspaso-${t.folio}`);
  }

  async downloadExcel(): Promise<void> {
    const data: any[] = [];
    for (const t of (this.dataSource.filteredData ?? []) as any[]) for (const i of (t.items ?? [])) data.push({
      folio: t.folio, date: fmtDate(t.createdOn), origin: t._origin, destination: t._destination, request: t._request,
      code: i.code, description: i.description, batch: i.batch ?? '', expiration: fmtDate(i.expiration, false), tag: i.tagRfid ?? '',
      missing: i.missing ? 'Sí' : '', guide: t.dhlGuide ?? '', status: t.status,
    });
    const cols = [['folio', 'Folio'], ['date', 'Fecha'], ['origin', 'Origen'], ['destination', 'Destino'], ['request', 'Solicitud'],
      ['code', 'Código'], ['description', 'Descripción'], ['batch', 'Lote'], ['expiration', 'Caducidad'], ['tag', 'Etiqueta'],
      ['missing', 'Faltante'], ['guide', 'Guía DHL'], ['status', 'Estado']].map(([k, n]) => ({ columnKey: k, columnName: n, type: 'string' as const }));
    await this.excel.simpleXLSX({ fileName: `Traspasos ${new Date().toISOString().slice(0, 10)}`,
      sheets: [{ sheetName: 'Traspasos', columns: cols as any, data, header: [], lineBreak: 0 }] });
  }
}
